<script>
export default {
	name: 'PromoListing',
	inheritAttrs: false,
};
</script>

<script setup>

import CrossSellBanner from '~/components/Banner/CrossSellBanner.vue';
import PromoListingCard from '~/components/PromoListing/Card.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import Dropdown from '~/components/form/Dropdown.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useRouteQuery } from '@vueuse/router';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import { useRouter, useRoute } from 'vue-router';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';

const props = defineProps({
	general: { type: Object, default: null },
	labels: { type: Object, default: null },
	promoChildPageProperties: { type: Array, default: null },
	promoTile: { type: Object, default: null },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	componentIdAA: { type: String, default: '' },
});
const router = useRouter();
const route = useRoute();

const { isEditorMode } = useGlobalAEMState();
const promoTypeTag = ref(useRouteQuery('promoType').value ? useRouteQuery('promoType').value : 'all');
const showListRange = ref(6);

const promoTypeList = computed(() => {
	return [
		{
			'label': props.labels.promoTypeAllLabel,
			'value': 'all',
		},
		...props.labels.promoTypeTagPathList.map((item) => ({
			'label': item.title,
			'value': item.name,
		})),
	];
});

const _applyFilterToData = (data, typeFilter) => {
	if (typeFilter == 'all') {
		return data;
	}
	return data.filter((item) => {
		let isShowTile = false;
		if (item.promoTag) {
			item.promoTag.forEach((tag) => {
				if (tag.name == typeFilter) {
					isShowTile = true;
				}
			});
		}
		return isShowTile;
	});
};

const _applyParamsToFilter = () => {
	if (promoTypeTag.value == 'all') {
		router.replace({ query: { ...route.query, promoType: undefined } });
	} else {
		router.replace({ query: { ...route.query, promoType: promoTypeTag.value } });
	}
};

const searchResultDisplayed = computed(() => {
	let result = [];
	result = _applyFilterToData(props.promoChildPageProperties, promoTypeTag.value);
	return result.filter((item) => !item.featured);
});

const searchResultDisplayedTruncated = computed(() => {
	return searchResultDisplayed.value.slice(0, showListRange.value);
});

const featuredPromo = computed(() => {
	return _applyFilterToData(props.promoChildPageProperties, promoTypeTag.value).filter((item) => item.featured);
});

const handleSelection = (value) => {
	promoTypeTag.value = value;
	showListRange.value = 6;
	_applyParamsToFilter();
};
const handleShowMore = () => {
	if (showListRange.value > searchResultDisplayed.value.length) return;
	showListRange.value = showListRange.value + 6;
};

const { isRTL } = useIsRTL();

</script>



<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>
<template v-else>
	<div
		v-if="((isMobileViewport && !props.isHideInMobile) ||
			(!isMobileViewport && !props.isHideInDesktop)) &&
			props.promoChildPageProperties.length > 0"
		class="PromoListing"
	>
		<div class="grid grid-cols-3 md:grid-cols-1 gap-x-6 generic-container mb-10 md:mb-8">
			<Dropdown
				v-if="props.labels.promoTypeTagPathList.length !== 0"
				:options="promoTypeList"
				:labelText="props.labels.promoTypeDropdownLabel"
				:rootAttrs="{
					class: '>md:col-start-2',
				}"
				:defaultValue="promoTypeTag"
				:aria-label="props.labels.promoTypeDropdownLabel"
				@change="handleSelection"
			></Dropdown>
		</div>
		<div v-if="featuredPromo.length > 0 || searchResultDisplayed.length > 0">
			<div
				v-if="featuredPromo.length > 0"
			>
				<AppHyperlink
					v-aa="[
						{
							clickName: featuredPromo[0].tileTitle,
							clickComponentType: 'URL',
							componentName: 'PromoListing',
							componentID: props.componentIdAA,
							promotionType: isUrlMatchCurrentHostname(featuredPromo[0].ctaUrl) ? 'internal' : 'external',
						},
						{
							name: featuredPromo[0].tileTitle,
							type: isUrlMatchCurrentHostname(featuredPromo[0].ctaUrl) ? 'other' : 'exit',
						},
					]"
					class="w-full hover:no-underline"
					:href="featuredPromo[0].ctaUrl"
				>
					<CrossSellBanner
						mobileBannerHeight="small"
						desktopBannerHeight="large"
						:bgStyle="props.general.featuredPromoSection.featuredPromoBStyle"
						:keyVisualSrc="props.general.featuredPromoSection.keyVisualImage"
						:keyVisualAlignment="props.general.featuredPromoSection.visualImagePosition"
					>
						<div class="md:pb-8">
							<h3 class="title >md:line-clamp-3">{{ featuredPromo[0].tileTitle }}</h3>
							<p
								v-html-sanitize="props.general.featuredPromoSection.promoDescription"
								class="author-html-container line-clamp-4"
							></p>
							<AppHyperlink
								v-aa="[
									{
										clickName: featuredPromo[0].tileTitle,
										clickComponentType: 'URL',
										componentName: 'PromoListing',
										componentID: props.componentIdAA,
										promotionType: isUrlMatchCurrentHostname(featuredPromo[0].ctaUrl) ? 'internal' : 'external',
									},
									{
										name: labels.exploreCta,
										type: isUrlMatchCurrentHostname(featuredPromo[0].ctaUrl) ? 'other' : 'exit',
									},
								]"
								class="cta-btn mt-6"
								:href="featuredPromo[0].ctaUrl"
							>
								<span>{{ labels.exploreCta }}</span>
								<icon-far-arrow-right
									class="cta-btn-arrow text-base inline-block fill-white ml-3 transform rtl:-rotate-180 rtl:ml-0 rtl:mr-3"
								/>
							</AppHyperlink>
						</div>
					</CrossSellBanner>
				</AppHyperlink>
			</div>
			<div class="mt-8 md:mt-6 grid grid-cols-3 gap-x-6 gap-y-7.5 md:grid-cols-1 generic-container">
				<PromoListingCard
					v-for="(item, index) in searchResultDisplayedTruncated"
					:key="index"
					:path="item.ctaUrl"
					:imageUrl="item.promoTileImage"
					:imageAltText="item.promoTileImageAltText"
					:title="item.tileTitle"
					:promoTileDescription="item.promoTileDescription"
					:promoTileIconText="item.promoTileIconText"
					:iconType="props.promoTile.iconType"
					:iconFontAwsomeId="props.promoTile.iconFontAwsomeId"
					:iconImage="props.promoTile.iconImage"
					:ctaLabel="labels.exploreCta"
					:componentIdAA="props.componentIdAA"
				/>
			</div>
			<div class="mt-10 md:mt-8">
				<CTAButton
					v-if="showListRange < searchResultDisplayed.length"
					:onClick="handleShowMore"
					:ctaText="props.labels.showMoreCta"
					sizeType="small"
					ctaLinkType="button"
				/>
			</div>
		</div>
		
		<div v-else>
			<div
				class="text-center py-16"
			></div>
		</div>
	</div>
</template>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.PromoListing {
	:deep(.CrossSellBanner) {
		display: flex;
	};
	:deep(.CrossSellBanner-inner) {
		width: 100%;
	}
}
.title {
	font-size: 2rem;
	line-height: 2.5rem;
	margin-bottom: 1rem
}
.cta-btn {
	color: var(--neutral-white-base);
	transition: .25s ease-out;

	&:hover {
		text-decoration: none;
		color: var(--primary-blue-extralight);

		.cta-btn-arrow {
			fill: var(--primary-blue-extralight);
			transform: translateX(4px);
			
			[dir="rtl"] & {
				transform: translateX(-4px) rotate(-180deg);
			}
		}
	}
}
.cta-btn-arrow {
	transition: .25s ease-out;
}
</style>
